import type { FlyerFieldConfig } from '@/types/flyer';

export interface PctRect { x: number; y: number; w: number; h: number }
export type Handle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';
export const MIN_SIZE = 0.01;

export const round3 = (n: number) => Math.round(n * 1000) / 1000;

export function fieldToRect(f: FlyerFieldConfig): PctRect {
  return { x: f.x_percent, y: f.y_percent, w: f.width_percent, h: f.height_percent };
}

export function rectToField<T extends FlyerFieldConfig>(f: T, r: PctRect): T {
  return {
    ...f,
    x_percent: round3(r.x), y_percent: round3(r.y),
    width_percent: round3(r.w), height_percent: round3(r.h),
  };
}

export function clampRect(r: PctRect): PctRect {
  const w = Math.min(1, Math.max(MIN_SIZE, r.w));
  const h = Math.min(1, Math.max(MIN_SIZE, r.h));
  const x = Math.min(1 - w, Math.max(0, r.x));
  const y = Math.min(1 - h, Math.max(0, r.y));
  return { x, y, w, h };
}

export function moveRect(r: PctRect, dx: number, dy: number): PctRect {
  return clampRect({ ...r, x: r.x + dx, y: r.y + dy });
}

// Edge-based resize: only the dragged edge(s) move; anchors stay fixed,
// growth is capped at the 0/1 boundary, and min size pins the moving edge.
export function resizeRect(r: PctRect, handle: Handle, dx: number, dy: number): PctRect {
  let left = r.x;
  let top = r.y;
  let right = r.x + r.w;
  let bottom = r.y + r.h;
  if (handle.includes('e')) right = Math.min(1, Math.max(left + MIN_SIZE, right + dx));
  if (handle.includes('w')) left = Math.max(0, Math.min(right - MIN_SIZE, left + dx));
  if (handle.includes('s')) bottom = Math.min(1, Math.max(top + MIN_SIZE, bottom + dy));
  if (handle.includes('n')) top = Math.max(0, Math.min(bottom - MIN_SIZE, top + dy));
  return { x: left, y: top, w: right - left, h: bottom - top };
}
